import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

const SUPPORT_EMAIL = 'admin@practcom.com';
const POLL_INTERVAL_MS = 15000; // 15s — pick up SamCart webhook reactivation quickly

/**
 * Fullscreen blocking gate shown when practice.subscription.status === "trial_expired".
 * - User can NOT dismiss it
 * - Subscribe button opens Plans & Billing (Stripe checkout)
 * - "I've subscribed — refresh" button + auto-polls /me every 15s for reactivation
 * - "Log out" link in corner
 */
export default function TrialExpiredGate({ children }) {
  const { practice, refreshPractice, logout, user } = useAuth();
  const [checking, setChecking] = useState(false);

  const status = practice?.subscription?.status;
  // Realtime fallback: if status is still "trial" but trialEndsAt has passed,
  // treat as expired immediately (the daily checker may not have run yet).
  const trialEndsAt = practice?.subscription?.trialEndsAt;
  const trialDateExpired = (() => {
    if (status !== 'trial' || !trialEndsAt) return false;
    try {
      return new Date(trialEndsAt).getTime() < Date.now();
    } catch {
      return false;
    }
  })();
  // Skip gate for SamCart-sourced accounts (they already paid) and beta accounts
  const isExempt =
    practice?.source === 'samcart' ||
    status === 'active' ||
    status === 'beta';
  const isExpired = !isExempt && (status === 'trial_expired' || trialDateExpired);

  const handleSubscribe = useCallback(() => {
    // New tiered pricing (Feb 2026): send users to the in-app Plans & Billing
    // page which spins up a Stripe Checkout session for the plan they pick.
    window.location.assign('/plans-billing');
  }, []);

  const handleCheckStatus = useCallback(async () => {
    setChecking(true);
    try {
      const updated = await refreshPractice();
      if (updated?.subscription?.status === 'active') {
        // Force a hard reload so all components re-fetch with fresh state
        window.location.reload();
      }
    } finally {
      setChecking(false);
    }
  }, [refreshPractice]);

  // Auto-poll while gate is showing
  useEffect(() => {
    if (!isExpired) return;
    const id = setInterval(() => {
      refreshPractice().then((updated) => {
        if (updated?.subscription?.status === 'active') {
          window.location.reload();
        }
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isExpired, refreshPractice]);

  if (!isExpired) return children;

  return (
    <>
      {/* Render dashboard underneath but make it inert/blurred for visual context */}
      <div aria-hidden="true" style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>

      {/* Blocking overlay */}
      <div
        data-testid="trial-expired-gate"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          background: 'rgba(15, 23, 42, 0.78)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        }}
      >
        <div
          style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '520px',
            width: '100%',
            padding: '40px 36px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            position: 'relative',
            textAlign: 'center',
          }}
        >
          {/* Logo / brand */}
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #2563eb, #1e40af)',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '28px',
              fontWeight: 'bold',
            }}
          >
            P
          </div>

          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#0f172a',
              margin: '0 0 12px 0',
              lineHeight: 1.3,
            }}
          >
            Your 15-Day Free Trial Has Ended
          </h1>

          <p
            style={{
              fontSize: '15px',
              color: '#475569',
              lineHeight: 1.6,
              margin: '0 0 24px 0',
            }}
          >
            Thank you for trying PractCom! To continue using your account and keep
            access to all your patient records, please subscribe below.
          </p>

          {/* Pricing card */}
          <div
            style={{
              background: '#f0f7ff',
              border: '2px solid #2563eb',
              borderRadius: '12px',
              padding: '20px',
              margin: '0 0 24px 0',
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#1e40af', lineHeight: 1.3 }}>
              Pick a plan that fits your practice
            </div>
            <div style={{ fontSize: '13px', color: '#475569', marginTop: '8px' }}>
              Cancel anytime · 15-day free trial · No credit card required
            </div>
          </div>

          {/* Primary CTA */}
          <Button
            data-testid="trial-expired-subscribe-btn"
            onClick={handleSubscribe}
            style={{
              width: '100%',
              background: '#2563eb',
              color: '#fff',
              padding: '16px',
              fontSize: '16px',
              fontWeight: 700,
              borderRadius: '10px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
              transition: 'all 0.2s',
              marginBottom: '12px',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#1d4ed8')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#2563eb')}
          >
            Subscribe Now — Choose a Plan
          </Button>

          {/* Already subscribed refresh */}
          <button
            data-testid="trial-expired-check-status-btn"
            onClick={handleCheckStatus}
            disabled={checking}
            style={{
              width: '100%',
              background: 'transparent',
              color: '#2563eb',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 600,
              borderRadius: '10px',
              border: '1.5px solid #2563eb',
              cursor: checking ? 'wait' : 'pointer',
              marginBottom: '20px',
            }}
          >
            {checking ? 'Checking…' : "I've subscribed — Check status"}
          </button>

          {/* Support note */}
          <div
            style={{
              borderTop: '1px solid #e2e8f0',
              paddingTop: '16px',
              fontSize: '13px',
              color: '#64748b',
              lineHeight: 1.5,
            }}
          >
            Need help or have questions?
            <br />
            Email{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=PractCom%20Subscription%20Question`}
              style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
              data-testid="trial-expired-support-email"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>

          {/* Log out (small, top-right) */}
          <button
            data-testid="trial-expired-logout-btn"
            onClick={logout}
            style={{
              position: 'absolute',
              top: '14px',
              right: '18px',
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '4px 8px',
            }}
          >
            Log out
          </button>
        </div>
      </div>
    </>
  );
}
